import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Accordion, Table, Badge, Alert, Row, Col, ProgressBar, Tabs, Tab, Form, Modal, Button } from 'react-bootstrap';
import { useUserProgress } from '@hooks';

/**
 * User Progress View Component
 * Displays detailed progress for a user in a course
 */
export function UserProgressView({ user, courseId, courseName }) {
  const [expandedAllQuestionsId, setExpandedAllQuestionsId] = useState(null);
  const [expandedModuleQuestionByModuleId, setExpandedModuleQuestionByModuleId] = useState({});
  const [incorrectOnlyByQuestionId, setIncorrectOnlyByQuestionId] = useState({});
  const [fullQuestionModal, setFullQuestionModal] = useState({ show: false, question: null });

  const {
    progress,
    moduleProgress,
    questionProgress,
    loading,
    error,
    statistics,
    getModuleQuestions,
  } = useUserProgress(user?.id, courseId);

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  /**
   * Get mastery level color
   */
  const getMasteryColor = (level) => {
    if (level >= 0.8) return 'success';
    if (level >= 0.5) return 'warning';
    return 'danger';
  };

  /**
   * Get question type label
   */
  const getQuestionTypeLabel = (type) => {
    const labels = {
      multiple_choice: 'Multiple Choice',
      true_false: 'True/False',
      fill_blank: 'Fill in Blank',
      matching: 'Matching',
      drag_drop: 'Drag & Drop',
      multiple_select: 'Multiple Select',
    };
    return labels[type] || type;
  };

  const truncateQuestionText = (text, maxLength = 60) => {
    const normalizedText = String(text || '');
    if (normalizedText.length <= maxLength) {
      return normalizedText;
    }
    return `${normalizedText.slice(0, maxLength)}...`;
  };

  /**
   * Format selected answer for display
   */
  const formatSelectedAnswer = (selectedAnswer) => {
    if (selectedAnswer === null || selectedAnswer === undefined) return 'N/A';
    
    if (typeof selectedAnswer === 'object') {
      if (Array.isArray(selectedAnswer)) {
        // Multiple select or matching answers
        return selectedAnswer.join(', ');
      }
      // Object (like matching answers)
      return Object.entries(selectedAnswer).map(([k, v]) => `${k} → ${v}`).join('; ');
    }

    // Primitive answer
    const str = String(selectedAnswer);
    if (str.length === 0) return '(empty)';
    return str.length > 100 ? `${str.slice(0, 100)}...` : str;
  };

  /**
   * Group attempts by display round (sessionHistoryId)
   */
  const groupAttemptsBySession = (rawHistory) => {
    const indexedHistory = rawHistory.map((attempt, index) => ({
      ...attempt,
      attemptNumber: index + 1,
    }));

    const grouped = {};
    indexedHistory.forEach((attempt) => {
      const sessionId = attempt.sessionHistoryId || 'session-unknown';
      if (!grouped[sessionId]) {
        grouped[sessionId] = [];
      }
      grouped[sessionId].push(attempt);
    });

    return Object.entries(grouped).map(([sessionId, attempts]) => ({
      sessionId,
      attempts,
      displayNumber: Object.keys(grouped).indexOf(sessionId) + 1,
      startTime: attempts[0].timestamp,
    }));
  };

  const toggleAllQuestionsExpansion = (questionId) => {
    setExpandedAllQuestionsId((currentQuestionId) => (
      currentQuestionId === questionId ? null : questionId
    ));
  };

  const toggleModuleQuestionExpansion = (moduleId, questionId) => {
    setExpandedModuleQuestionByModuleId((currentState) => ({
      ...currentState,
      [moduleId]: currentState[moduleId] === questionId ? null : questionId,
    }));
  };

  const toggleIncorrectOnlyFilter = (questionId) => {
    setIncorrectOnlyByQuestionId((currentState) => ({
      ...currentState,
      [questionId]: !currentState[questionId],
    }));
  };

  const getAttemptRows = (question) => {
    const rawHistory = Array.isArray(question?.attemptHistory) ? question.attemptHistory : [];
    const indexedHistory = rawHistory.map((attempt, index) => ({
      ...attempt,
      attemptNumber: index + 1,
    }));

    if (incorrectOnlyByQuestionId[question.questionId]) {
      return indexedHistory.filter((attempt) => !attempt.isCorrect && !attempt.skipped);
    }

    return indexedHistory;
  };

  const renderAttemptHistoryPanel = (question, colSpan, switchIdPrefix) => {
    const rawHistory = Array.isArray(question?.attemptHistory) ? question.attemptHistory : [];
    const visibleHistory = getAttemptRows(question);
    const showIncorrectOnly = Boolean(incorrectOnlyByQuestionId[question.questionId]);
    const sessionGroups = groupAttemptsBySession(rawHistory);

    return (
      <tr>
        <td colSpan={colSpan} className="bg-light">
          <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h6 className="mb-0">
                Attempt History 
                <span className="ms-2">
                  <Badge bg="secondary">{question.attempts} total attempts</Badge>
                </span>
              </h6>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <small className="text-muted">
                  {visibleHistory.length} of {rawHistory.length} attempt{rawHistory.length === 1 ? '' : 's'} shown
                </small>
                <Form.Check
                  type="switch"
                  id={`${switchIdPrefix}-incorrect-only-${question.questionId}`}
                  label="Incorrect only"
                  checked={showIncorrectOnly}
                  onChange={() => toggleIncorrectOnlyFilter(question.questionId)}
                />
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={() => setFullQuestionModal({ show: true, question })}
                >
                  View Full Question
                </Button>
              </div>
            </div>

            {visibleHistory.length === 0 ? (
              <p className="text-muted mb-0">
                {showIncorrectOnly
                  ? 'No incorrect attempts found for this question.'
                  : 'No attempt history found for this question.'}
              </p>
            ) : (
              <div className="attempt-groups">
                {sessionGroups.map((group) => {
                  const groupAttempts = showIncorrectOnly 
                    ? group.attempts.filter(a => !a.isCorrect && !a.skipped)
                    : group.attempts;
                  
                  if (groupAttempts.length === 0 && showIncorrectOnly) {
                    return null;
                  }

                  return (
                    <div key={group.sessionId} className="mb-4">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Badge bg="info">Display #{group.displayNumber}</Badge>
                        <small className="text-muted">
                          {new Date(group.startTime).toLocaleString()}
                        </small>
                      </div>
                      <Table size="sm" striped bordered hover className="mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: '3rem' }}>Attempt</th>
                            <th>Result</th>
                            <th>Answer Selected</th>
                            <th>Hints Used</th>
                            <th style={{ width: '6rem' }}>Score</th>
                            <th>Timestamp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupAttempts.map((attempt) => (
                            <tr key={`${question.questionId}-attempt-${attempt.attemptNumber}`}>
                              <td className="text-center">{attempt.attemptNumber}</td>
                              <td>
                                {attempt.skipped ? (
                                  <Badge bg="secondary">Skipped</Badge>
                                ) : (
                                  <Badge bg={attempt.isCorrect ? 'success' : 'danger'}>
                                    {attempt.isCorrect ? 'Correct' : 'Incorrect'}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                {attempt.skipped ? (
                                  <span className="text-muted fst-italic">—skipped—</span>
                                ) : attempt.isCorrect ? (
                                  <span className="text-muted">—</span>
                                ) : (
                                  <code className="text-break" style={{ fontSize: '0.85rem' }}>
                                    {formatSelectedAnswer(attempt.selectedAnswer)}
                                  </code>
                                )}
                              </td>
                              <td className="text-center">{attempt.hintsUsed || 0}</td>
                              <td className="text-center">
                                {attempt.scoreAttempt !== null && attempt.scoreAttempt !== undefined
                                  ? `${Math.round(attempt.scoreAttempt)}%`
                                  : '—'
                                }
                              </td>
                              <td style={{ fontSize: '0.85rem' }}>{formatDate(attempt.timestamp)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return <div className="text-center py-4">Loading progress...</div>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!progress) {
    return (
      <Alert variant="info">
        {user?.name} has not started this course yet.
      </Alert>
    );
  }

  return (
    <div>
      {/* Full Question Modal */}
      <Modal show={fullQuestionModal.show} onHide={() => setFullQuestionModal({ show: false, question: null })} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Full Question Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fullQuestionModal.question && (
            <div>
              <div className="mb-3">
                <h6 className="text-muted">Question Type</h6>
                <Badge bg="info" className="me-2">
                  {getQuestionTypeLabel(fullQuestionModal.question.questionType)}
                </Badge>
              </div>
              <div className="mb-3">
                <h6 className="text-muted">Question Text</h6>
                <div className="p-3 bg-light rounded">
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                    {fullQuestionModal.question.questionText}
                  </p>
                </div>
              </div>
              <div className="mb-3">
                <h6 className="text-muted">Performance Summary</h6>
                <Row>
                  <Col md={6}>
                    <small className="text-muted">Total Attempts:</small>
                    <p className="mb-0 fw-bold">{fullQuestionModal.question.attempts}</p>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted">Success Rate:</small>
                    <p className="mb-0 fw-bold">
                      {fullQuestionModal.question.attempts > 0
                        ? `${Math.round((fullQuestionModal.question.correctAttempts / fullQuestionModal.question.attempts) * 100)}%`
                        : 'N/A'}
                    </p>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setFullQuestionModal({ show: false, question: null })}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Overall Statistics */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">
            {user?.name}&apos;s Progress in {courseName}
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <div className="text-center">
                <h4>{Math.round(statistics.overallScore)}%</h4>
                <small className="text-muted">Overall Score</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4>{statistics.completedModules} / {statistics.totalModules}</h4>
                <small className="text-muted">Modules Completed</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4>{statistics.totalAttempts}</h4>
                <small className="text-muted">Total Attempts</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4>
                  {statistics.totalAttempts > 0
                    ? Math.round((statistics.correctAttempts / statistics.totalAttempts) * 100)
                    : 0}%
                </h4>
                <small className="text-muted">Success Rate</small>
              </div>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <small className="text-muted">
                Last Activity: {formatDate(progress.lastActivityAt)}
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Detailed Progress Tabs */}
      <Tabs defaultActiveKey="modules" className="mb-3">
        {/* Module Progress Tab */}
        <Tab eventKey="modules" title="Module Progress">
          <Card>
            <Card.Body>
              {moduleProgress.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No module progress data available.
                </div>
              ) : (
                <Accordion>
                  {moduleProgress.map((module) => (
                    <Accordion.Item eventKey={module.moduleId} key={module.moduleId}>
                      <Accordion.Header>
                        <div className="d-flex justify-content-between align-items-center w-100 me-3">
                          <span>
                            {module.moduleName}
                            {module.isCompleted && (
                              <Badge bg="success" className="ms-2">Completed</Badge>
                            )}
                            {!module.isUnlocked && (
                              <Badge bg="secondary" className="ms-2">Locked</Badge>
                            )}
                          </span>
                          <span className="text-muted">
                            Score: {Math.round(module.score)}%
                          </span>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Row className="mb-3">
                          <Col md={6}>
                            <strong>Questions:</strong> {module.completedQuestions} / {module.totalQuestions}
                          </Col>
                          <Col md={6}>
                            <strong>Last Activity:</strong> {formatDate(module.lastActivity)}
                          </Col>
                        </Row>
                        <ProgressBar
                          now={module.totalQuestions > 0
                            ? Math.min(100, (module.completedQuestions / module.totalQuestions) * 100)
                            : 0}
                          label={module.totalQuestions > 0
                            ? `${Math.round(Math.min(100, (module.completedQuestions / module.totalQuestions) * 100))}%`
                            : '0%'}
                        />
                        
                        {/* Questions in this module */}
                        <div className="mt-3">
                          <h6>Questions in this Module:</h6>
                          {getModuleQuestions(module.moduleId).length === 0 ? (
                            <p className="text-muted">No questions attempted yet.</p>
                          ) : (
                            <Table size="sm" striped>
                              <thead>
                                <tr>
                                  <th style={{ width: '5rem' }}>Details</th>
                                  <th>Question</th>
                                  <th>Type</th>
                                  <th>Attempts</th>
                                  <th>Correct</th>
                                   <th>Skipped</th>
                                   <th>Mastery</th>
                                </tr>
                              </thead>
                              <tbody>
                                {getModuleQuestions(module.moduleId).map((q) => {
                                  const isExpanded = expandedModuleQuestionByModuleId[module.moduleId] === q.questionId;

                                  return (
                                    <React.Fragment key={q.questionId}>
                                      <tr
                                        onClick={() => toggleModuleQuestionExpansion(module.moduleId, q.questionId)}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <td>
                                          <Badge bg={isExpanded ? 'dark' : 'secondary'} pill>
                                            {isExpanded ? 'Hide' : 'View'}
                                          </Badge>
                                        </td>
                                        <td>{truncateQuestionText(q.questionText, 50)}</td>
                                        <td>
                                          <Badge bg="info" pill>
                                            {getQuestionTypeLabel(q.questionType)}
                                          </Badge>
                                        </td>
                                        <td>{q.attempts}</td>
                                        <td>{q.correctAttempts}</td>
                                          <td>{q.timesSkipped ?? 0}</td>
                                        <td>
                                          <Badge bg={getMasteryColor(q.masteryLevel)}>
                                            {Math.round(q.masteryLevel * 100)}%
                                          </Badge>
                                        </td>
                                      </tr>
                                      {isExpanded && renderAttemptHistoryPanel(q, 7, `module-${module.moduleId}`)}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </Table>
                          )}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* All Questions Tab */}
        <Tab eventKey="questions" title="All Questions">
          <Card>
            <Card.Body>
              {questionProgress.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No questions attempted yet.
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: '5rem' }}>Details</th>
                      <th>Module</th>
                      <th>Question</th>
                      <th>Type</th>
                      <th>Attempts</th>
                      <th>Correct Attempts</th>
                      <th>Skipped</th>
                      <th>Success Rate</th>
                      <th>Mastery Level</th>
                      <th>Last Attempt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questionProgress.map((q) => {
                      const isExpanded = expandedAllQuestionsId === q.questionId;

                      return (
                        <React.Fragment key={q.questionId}>
                          <tr
                            onClick={() => toggleAllQuestionsExpansion(q.questionId)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <Badge bg={isExpanded ? 'dark' : 'secondary'} pill>
                                {isExpanded ? 'Hide' : 'View'}
                              </Badge>
                            </td>
                            <td>{q.moduleName}</td>
                            <td>{truncateQuestionText(q.questionText, 60)}</td>
                            <td>
                              <Badge bg="info" pill>
                                {getQuestionTypeLabel(q.questionType)}
                              </Badge>
                            </td>
                            <td>{q.attempts}</td>
                            <td>{q.correctAttempts}</td>
                              <td>{q.timesSkipped ?? 0}</td>
                            <td>
                              {q.attempts > 0
                                ? `${Math.round((q.correctAttempts / q.attempts) * 100)}%`
                                : 'N/A'}
                            </td>
                            <td>
                              <Badge bg={getMasteryColor(q.masteryLevel)}>
                                {Math.round(q.masteryLevel * 100)}%
                              </Badge>
                            </td>
                            <td>{formatDate(q.lastAttemptAt)}</td>
                          </tr>

                          {isExpanded && renderAttemptHistoryPanel(q, 10, 'all-questions')}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
}

UserProgressView.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  courseId: PropTypes.string,
  courseName: PropTypes.string,
};
